import { useNavigate } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import FloatingNotifications from '@/components/ui/floating-notifications';
import { Header } from '@/components/landing/Header';
import CalendlyEmbed from '@/components/ui/CalendlyEmbed';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Grid Background */}
      <div 
        className="absolute inset-0 w-full"
        style={{
          backgroundImage: `
            linear-gradient(rgb(100 100 100 / 0.03) 1px, transparent 1px),
            linear-gradient(to right, rgb(100 100 100 / 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          zIndex: -1
        }}
      />
      
      <Header />
      
      {/* Main content */}
      <main className="flex-1 pt-[72px]">
        {/* Hero Section */}
        <section className="relative min-h-screen">

          {/* Content Container */}
          <div className="container mx-auto px-4 pt-8 pb-24 md:pt-12 md:pb-32 relative z-10">
            <div className="mx-auto max-w-[64rem] space-y-8">
              {/* New Badge */}
              <div className="flex justify-center">
                <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-800 px-3 py-1 text-sm text-muted-foreground">
                  <span className="mr-2 h-1.5 w-1.5 rounded-full bg-primary animate-flicker"></span>
                  <b>New</b>: AI-powered client intake automation for law firms
                </span>
              </div>
              
              {/* Hero Title */}
              <h1 className="text-center text-4xl font-medium tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
              Document Intake Automation{' '}
                <span className="bg-gradient-to-r from-primary/60 to-primary bg-clip-text text-transparent">
                  for Law Firms
                </span>
              </h1>
              
              {/* Hero Description */}
              <p className="mx-auto max-w-2xl text-center text-lg text-muted-foreground font-light leading-relaxed">
                We automate client intake so your team stops chasing signatures and paperwork. Retainers, medical releases, and documents — sent, signed, and collected automatically.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Button
                  onClick={() => window.open('https://cal.com/levot-labs-cptefe/30min?overlayCalendar=true', '_blank')}
                  className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-8 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 group"
                >
                  Book a Call
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/waitlist')}
                  className="inline-flex h-11 items-center justify-center rounded-full border border-gray-200 dark:border-gray-800 px-8 text-sm font-medium transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  Get Started
                </Button>
              </div>

              {/* Trusted By Section */}
             {/* <div className="mt-24 space-y-4">
                <p className="text-center text-sm text-muted-foreground font-light">
                  Trusted by underwriting and operations teams
                </p>
                <div className="relative mx-auto max-w-2xl">
                  {/* Fade gradients 
                  <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10"></div>
                  <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10"></div>
                  
                  {/* Scrolling content 
                  <div className="overflow-hidden relative">
                    <div className="flex whitespace-nowrap animate-marquee">
                      {[...Array(2)].flatMap((_, i) => (
                        ['Harbor Insurance', 'Atlas Lending', 'ClearRisk', 'NorthBridge', 'Pioneer Credit', 'Summit Mutual'].map((company) => (
                          <span 
                            key={`${company}-${i}`} 
                            className="inline-block mx-4 text-xl font-semibold tracking-tight text-muted-foreground/50"
                          >
                            {company}
                          </span>
                        ))
                      ))}
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Video Section 
              <div className="relative mt-8">
                <div className="relative mx-auto max-w-4xl rounded-xl overflow-hidden">
                  <div className="aspect-[1.91/1]">
                    <video 
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      className="w-full h-full object-cover rounded-xl border border-gray-200"
                      src="/lovable-uploads/levotexample.mp4"
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </div>

              {/* Notifications Section */}
              <section className="container mx-auto px-4 pb-0">
                <div className="relative h-[150px]">
                  <FloatingNotifications />
                </div>
              </section>

              {/* Stats Section */}
              <section className="container mx-auto px-4 pt-2">
                <div className="mx-auto max-w-[64rem]">
                  <div className="rounded-2xl bg-white p-12 shadow-[0_1px_3px_0_rgb(0,0,0,0.1)]">
                    {/* Header */}
                    <div className="space-y-4">
                      <span className="text-base text-muted-foreground">
                        From chasing paperwork to automated client intake
                      </span>
                      <h2 className="text-4xl font-medium tracking-tight">
                        A modern AI stack for law firm automation
                      </h2>
                      <p className="text-xl text-muted-foreground max-w-3xl">
                        Levot automates document collection, e-signatures, and client communications so your team can focus on cases, not admin work.
                      </p>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-6">
                        {/* Stat 1 */}
                        <div className="space-y-2">
                          <h3 className="text-4xl font-medium text-[#f97316]">85%</h3>
                          <h4 className="font-medium">Less admin time</h4>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            Automated document requests and follow-ups eliminate manual chasing.
                          </p>
                        </div>

                        {/* Stat 2 */}
                        <div className="space-y-2">
                          <h3 className="text-4xl font-medium text-[#f97316]">24hr</h3>
                          <h4 className="font-medium">Average intake time</h4>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            Clients complete retainers and releases faster with mobile-friendly workflows.
                          </p>
                        </div>

                        {/* Stat 3 */}
                        <div className="space-y-2">
                          <h3 className="text-4xl font-medium text-[#f97316]">99%</h3>
                          <h4 className="font-medium">Document completion</h4>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            Smart reminders and follow-ups ensure nothing falls through the cracks.
                          </p>
                        </div>

                        {/* Stat 4 */}
                        <div className="space-y-2">
                          <h3 className="text-4xl font-medium text-[#f97316]">100%</h3>
                          <h4 className="font-medium">Compliant records</h4>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            Every signature and document is timestamped with full audit trails.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Data Quality Section */}
              <section className="container mx-auto px-4 py-16">
                <div className="mx-auto max-w-[64rem] space-y-12">
                  {/* Section Header */}
                  <div className="text-center space-y-4">
                    <h2 className="text-5xl font-medium tracking-tight">
                      Client intake automation built for{' '}<br />
                      <span className="font-light italic">modern</span>{' '}
                      <span className="text-gray-500">law firms.</span>
                    </h2>
                    <p className="text-xl text-muted-foreground">
                      Send, track, and collect documents automatically with complete audit trails.
                    </p>
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
                    {/* Feature 1 */}
                    <div className="space-y-6">
                      <div className="aspect-square bg-gray-50 rounded-xl p-8 relative">
                        <img 
                          src="/lovable-uploads/1.png"
                          alt="Automated document requests"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-2xl font-medium">Automated document requests</h3>
                        <p className="text-muted-foreground">
                          Retainers, medical releases, and intake forms sent automatically via text and email.
                        </p>
                        <div className="flex items-center text-gray-600 hover:text-gray-900 cursor-pointer group" onClick={() => window.open('https://cal.com/levot-labs-cptefe/30min?overlayCalendar=true', '_blank')}>
                          <span className="text-sm font-medium">Talk with an expert</span>
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </div>

                    {/* Feature 2 */}
                    <div className="space-y-6">
                      <div className="aspect-square bg-gray-50 rounded-xl p-8 relative">
                        <img 
                          src="/lovable-uploads/2.png"
                          alt="E-signature collection"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-2xl font-medium">E-signature collection</h3>
                        <p className="text-muted-foreground">
                          Clients sign documents on any device with legally compliant e-signatures and timestamps.
                        </p>
                        <div className="flex items-center text-gray-600 hover:text-gray-900 cursor-pointer group" onClick={() => window.open('https://cal.com/levot-labs-cptefe/30min?overlayCalendar=true', '_blank')}>
                          <span className="text-sm font-medium">See the workflow</span>
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </div>

                    {/* Feature 3 */}
                    <div className="space-y-6">
                      <div className="flex-1">
                        <div className="aspect-square bg-gray-50 rounded-xl p-8 relative">
                          <img 
                            src="/lovable-uploads/3.png"
                            alt="Smart follow-ups"
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-2xl font-medium">Smart follow-ups</h3>
                        <p className="text-muted-foreground">
                          Automated reminders via text and email until documents are completed — no manual chasing.
                        </p>
                        <div className="flex items-center text-gray-600 hover:text-gray-900 cursor-pointer group" onClick={() => window.open('https://cal.com/levot-labs-cptefe/30min?overlayCalendar=true', '_blank')}>
                          <span className="text-sm font-medium">Book a demo</span>
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

{/*<div className="relative w-full overflow-hidden py-12 bg-white">
  {/* Gradient Masks 
  <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10" />
  <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10" />
  
  {/* Scrolling Container 
  <div className="flex gap-8 animate-scroll">
    {/* First Set of Videos 
    <div className="flex gap-8 min-w-full">
      <div className="w-[400px] space-y-3">
        <img 
          src="/seerlonglogo.png" 
          alt="Seer Logo" 
          className="h-8 w-auto mx-auto"
        />
        <p className="text-sm text-muted-foreground text-center">Document intake automation for insurance and lending</p>
        <div className="rounded-lg overflow-hidden">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover rounded-xl border border-gray-200"
            src="/lovable-uploads/levotexample.mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
      <div className="w-[400px] space-y-3">
        <img 
          src="/lovable-uploads/testimonial2.png" 
          alt="Testimonial 1" 
          className="h-8 w-auto mx-auto"
        />
        <p className="text-sm text-muted-foreground text-center">Underwriting workbench with AI-assisted review</p>
        <div className="rounded-lg overflow-hidden">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover rounded-xl border border-gray-200"
            src="/lovable-uploads/scrape1.mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
      <div className="w-[400px] space-y-3">
        <img 
          src="/lovable-uploads/testimonial4.png" 
          alt="Testimonial 2" 
          className="h-8 w-auto mx-auto"
        />
        <p className="text-sm text-muted-foreground text-center">Compliance-ready audit trails and evidence packs</p>
        <div className="rounded-lg overflow-hidden">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover rounded-xl border border-gray-200"
            src="/lovable-uploads/exampleresponse.mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
    
    {/* Duplicate Set - Exact copy of the first set 
    <div className="flex gap-8 min-w-full">
      <div className="w-[400px] space-y-3">
        <img 
          src="/seerlonglogo.png" 
          alt="Seer Logo" 
          className="h-8 w-auto mx-auto"
        />
        <p className="text-sm text-muted-foreground text-center">Document intake automation for insurance and lending</p>
        <div className="rounded-lg overflow-hidden">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover rounded-xl border border-gray-200"
            src="/lovable-uploads/levotexample.mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
      <div className="w-[400px] space-y-3">
        <img 
          src="/lovable-uploads/testimonial2.png" 
          alt="Testimonial 1" 
          className="h-8 w-auto mx-auto"
        />
        <p className="text-sm text-muted-foreground text-center">Underwriting workbench with AI-assisted review</p>
        <div className="rounded-lg overflow-hidden">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover rounded-xl border border-gray-200"
            src="/lovable-uploads/scrape1.mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
      <div className="w-[400px] space-y-3">
        <img 
          src="/lovable-uploads/testimonial4.png" 
          alt="Testimonial 2" 
          className="h-8 w-auto mx-auto"
        />
        <p className="text-sm text-muted-foreground text-center">Compliance-ready audit trails and evidence packs</p>
        <div className="rounded-lg overflow-hidden">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover rounded-xl border border-gray-200"
            src="/lovable-uploads/exampleresponse.mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
  </div>
</div>


              {/* Calendly Section *
              <section className="container mx-auto px-4 py-16 bg-white">
                <div className="mx-auto max-w-[64rem] space-y-8 text-center">
                  {/* Header 
                  <div className="space-y-4">
                    <h2 className="text-4xl font-medium">Book a demo</h2>
                    <p className="text-lg text-muted-foreground">
                      Walk through your workflow and quantify time saved.
                    </p>
                  </div>

                  {/* Calendly Widget 
                  <div className="mt-8">
                    <CalendlyEmbed url="https://cal.com/levot-labs-cptefe/30min?overlayCalendar=true" />
                  </div>
                </div>
              </section>*/}

              {/* Pricing Section */}
              <section id="pricing" className="container mx-auto px-4">
                <div className="mx-auto max-w-[64rem] space-y-8 text-center">
                  {/* Header */}
                  <div className="space-y-4">
                    <div className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-800 px-4 py-1.5">
                      <span className="text-sm font-medium">Automation Plans</span>
                    </div>
                    <h2 className="text-4xl font-medium">Two plans for law firm automation</h2>
                    <p className="text-lg text-muted-foreground">
                      Both plans include setup, customization, and ongoing support.
                    </p>
                  </div>

                  {/* Pricing Cards */}
                  <div className="grid md:grid-cols-2 gap-8 mt-12">
                    {/* Intake Starter Plan */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-background p-8">
                      <div className="space-y-6">
                        {/* Logo */}
                        {/*<div className="flex justify-center">
                          <img 
                            src="/lovable-uploads/a2.png"
                            alt="Intake Starter"
                            className="h-16 w-auto mb-4"
                          />
                        </div>*/}
                        <div>
                          <h3 className="text-2xl font-medium">Intake Starter</h3>
                          <p className="text-muted-foreground mt-1">Launch client intake automation quickly</p>
                        </div>

                        <div className="space-y-1">
                          <p className="text-5xl font-medium">$2,497</p>
                          <p className="text-muted-foreground">per month</p>
                        </div>

                        <div className="mt-8">
                          <Button
                            onClick={() => navigate('/waitlist')}
                            className="inline-flex h-11 w-full items-center justify-center rounded-full bg-primary px-8 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                          >
                            Get Started
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>

                        <ul className="space-y-4 pt-8">
                          {[
                            'Automated retainer & release delivery',
                            'E-signature collection',
                            'SMS and email follow-ups',
                            'Client intake forms',
                            'Document tracking dashboard',
                            'Secure cloud storage',
                          ].map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <svg className="h-5 w-5 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                              <span className="text-muted-foreground text-sm text-left">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Full Practice Suite */}
                    <div className="rounded-xl border-2 border-primary bg-background p-8 relative">
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                          MOST POPULAR
                        </span>
                      </div>
                      <div className="space-y-6">
                        {/* Logo */}
                        {/*<div className="flex justify-center">
                          <img 
                            src="/lovable-uploads/Levot3.png"
                            alt="Full Practice Suite"
                            className="h-16 w-auto mb-4"
                          />
                        </div>*/}
                        <div>
                          <h3 className="text-2xl font-medium">Full Practice Suite</h3>
                          <p className="text-muted-foreground mt-1">Complete automation with integrations</p>
                        </div>

                        <div className="space-y-1">
                          <p className="text-5xl font-medium">$4,497</p>
                          <p className="text-muted-foreground">per month</p>
                        </div>

                        <div className="mt-8">
                          <Button
                            onClick={() => navigate('/waitlist')}
                            className="inline-flex h-11 w-full items-center justify-center rounded-full bg-primary px-8 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                          >
                            Get Started
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>

                        <ul className="space-y-4 pt-8">
                          {[
                            'All Intake Starter features',
                            'Case management integrations',
                            'Medical records requests',
                            'Custom document workflows',
                            'Multi-language support',
                            'Priority response times',
                            'Dedicated account manager',
                          ].map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <svg className="h-5 w-5 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                              <span className="text-muted-foreground text-sm text-left">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Secure Payment Note */}
                  <div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0110 0v4"></path>
                    </svg>
                    Data is encrypted in transit and at rest — HIPAA compliant
                  </div>
                </div>
              </section>

              {/* Footer */}
              <footer className="relative bg-white border-t border-gray-200">
                <div className="container mx-auto px-4 py-12">
                  <div className="flex flex-col items-center gap-8">
                    {/* Logo */}
                    <img 
                      src="/lovable-uploads/levot_logo.png"
                      alt="Levot Logo" 
                      className="h-12 w-auto"
                    />
                    
                    {/* Links */}
                    <ul className="flex flex-wrap justify-center items-center gap-8">
                      <li className="flex items-center">
                        <button 
                          onClick={() => navigate('/waitlist')} 
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Legal & NDAs
                        </button>
                      </li>
                      <li className="flex items-center">
                        <button 
                          onClick={() => window.open('https://cal.com/levot-labs-cptefe/30min?overlayCalendar=true', '_blank')} 
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Book a Call
                        </button>
                      </li>
                      <li className="flex items-center">
                        <button 
                          onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} 
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Pricing
                        </button>
                      </li>
                      <li className="flex items-center">
                        <a 
                          href="mailto:inquiry@levotlabs.com" 
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
                        >
                          <Mail className="h-4 w-4" />
                          inquiry@levotlabs.com
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </footer>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Landing;
